/**
 * Selector chico de Ctrl/⌘+K mientras el Agente CEO siga usando esa combinación:
 * "Abrir Agente CEO" o "Buscar menú". El Super Administrador ve además la opción
 * de mover el agente a Alt+K (⌥K en Mac) para todos (con confirmación); al hacerlo,
 * este selector ya no vuelve a salir para nadie.
 *
 * Qué opciones salen lo decide `resolverAtajo` (`lib/atajosTeclado.ts`).
 */
import { useState } from "react";
import { Keyboard, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCambiarAtajoAgenteCeo } from "@/hooks/useAtajoAgenteCeo";
import { esMac, etiquetaCombinacion, type OpcionSelector } from "@/lib/atajosTeclado";

interface Props {
  /** null = cerrado. */
  opciones: OpcionSelector[] | null;
  onCerrar: () => void;
  onElegir: (opcion: OpcionSelector) => void;
}

export function SelectorAtajoK({ opciones, onCerrar, onElegir }: Props) {
  const [mac] = useState(esMac);
  const [confirmando, setConfirmando] = useState(false);
  const cambiar = useCambiarAtajoAgenteCeo();
  const nuevo = etiquetaCombinacion("alt+k", mac);
  const actual = etiquetaCombinacion("mod+k", mac);

  const textos: Record<OpcionSelector, { titulo: string; detalle: string; Icono: typeof Search }> = {
    agenteCeo: { titulo: "Abrir Agente CEO", detalle: "Pregúntale sobre esta pantalla", Icono: Sparkles },
    buscador: { titulo: "Buscar menú", detalle: "Ir a cualquier menú al que tienes acceso", Icono: Search },
    cambiarAtajoCeo: {
      titulo: `Cambiar el atajo del Agente CEO a ${nuevo}`,
      detalle: `Para todos los usuarios. ${actual} quedará para el buscador de menús`,
      Icono: Keyboard,
    },
  };

  const elegir = (opcion: OpcionSelector) => {
    if (opcion === "cambiarAtajoCeo") {
      onCerrar();
      setConfirmando(true);
      return;
    }
    onElegir(opcion);
  };

  const confirmar = () => {
    cambiar.mutate(undefined, {
      onSuccess: () => {
        setConfirmando(false);
        toast.success(`Listo: el Agente CEO ahora abre con ${nuevo} y ${actual} es el buscador de menús.`);
      },
      onError: (err: { code?: string; message?: string }) => {
        const texto =
          err?.code === "42501"
            ? "Solo el Super Administrador puede cambiar el atajo."
            : err?.code === "PGRST202" || err?.code === "42883"
              ? "Todavía no está aplicada la migración del atajo en esta base de datos."
              : "No pudimos cambiar el atajo. Intenta de nuevo.";
        toast.error(texto);
      },
    });
  };

  return (
    <>
      <Dialog open={opciones !== null} onOpenChange={(v) => !v && onCerrar()}>
        <DialogContent data-buscador-menus="" className="max-w-sm gap-0 overflow-hidden p-0 sm:top-[22%] sm:translate-y-0">
          <DialogTitle className="border-b px-4 py-3 pr-12 text-xs font-medium text-muted-foreground">
            ¿Qué quieres abrir con {actual}?
          </DialogTitle>
          <DialogDescription className="sr-only">
            Usa las flechas para elegir, Enter para abrir y Esc para cerrar.
          </DialogDescription>
          <Command loop label="Elegir qué abre el atajo">
            {/* Sin input: el foco va a la lista y cmdk maneja flechas y Enter. */}
            <CommandList className="max-h-none" aria-label="Opciones del atajo" tabIndex={0}>
              <CommandGroup>
                {(opciones ?? []).map((o) => {
                  const { titulo, detalle, Icono } = textos[o];
                  return (
                    <CommandItem key={o} value={o} onSelect={() => elegir(o)} className="gap-3 py-2.5">
                      <Icono className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="flex min-w-0 flex-col">
                        <span className="text-sm">{titulo}</span>
                        <span className="text-xs text-muted-foreground">{detalle}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmando} onOpenChange={(v) => !cambiar.isPending && setConfirmando(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar el atajo del Agente CEO</AlertDialogTitle>
            <AlertDialogDescription>
              Este cambio es definitivo y aplica para todos los usuarios. El Agente CEO quedará en {nuevo} y{" "}
              {actual} será el buscador de menús en todo el sistema, también en el Portal Alta Dirección. No se
              puede deshacer desde el panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cambiar.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={cambiar.isPending}
              onClick={(e) => {
                e.preventDefault();
                confirmar();
              }}
            >
              {cambiar.isPending ? "Cambiando" : "Sí, cambiar el atajo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
